/**
 * ShiftDialog Component - Add/Edit shift modal
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Trash2 } from 'lucide-react';

// Station options
const STATIONS = [
  { value: 'none', label: 'No Station' },
  { value: 'kitchen', label: 'Kitchen' },
  { value: 'bar', label: 'Bar' },
  { value: 'front_desk', label: 'Front Desk' },
  { value: 'expo', label: 'Expo' },
  { value: 'drive_thru', label: 'Drive-Thru' },
  { value: 'patio', label: 'Patio' },
];

// Role options
const ROLES = [
  { value: 'server', label: 'Server' },
  { value: 'cook', label: 'Cook' },
  { value: 'chef', label: 'Chef' },
  { value: 'host', label: 'Host' },
  { value: 'manager', label: 'Manager' },
  { value: 'driver', label: 'Driver' },
  { value: 'cashier', label: 'Cashier' },
];

export default function ShiftDialog({
  open,
  onOpenChange,
  editingShift,
  shiftForm,
  setShiftForm,
  employees,
  onSave,
  onDelete,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg">
            {editingShift ? 'Edit Shift' : 'Add New Shift'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-3 sm:space-y-4 py-2 sm:py-4">
          {/* Employee Select */}
          <div className="space-y-1.5 sm:space-y-2">
            <Label className="text-sm">Employee</Label>
            <Select 
              value={shiftForm.employee_id} 
              onValueChange={(v) => setShiftForm(prev => ({ ...prev, employee_id: v }))}
              disabled={!!editingShift}
            >
              <SelectTrigger className="bg-slate-800 border-slate-700 h-9 sm:h-10" data-testid="shift-employee-select">
                <SelectValue placeholder="Select employee" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {employees.map(emp => (
                  <SelectItem key={emp.id} value={String(emp.id)} className="text-white">
                    {emp.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date */}
          <div className="space-y-1.5 sm:space-y-2">
            <Label className="text-sm">Date</Label>
            <Input
              type="date"
              value={shiftForm.date}
              onChange={(e) => setShiftForm(prev => ({ ...prev, date: e.target.value }))}
              className="bg-slate-800 border-slate-700 h-9 sm:h-10"
              disabled={!!editingShift}
              data-testid="shift-date-input"
            />
          </div>

          {/* Time Range */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-1.5 sm:space-y-2">
              <Label className="text-sm">Start</Label>
              <Input
                type="time"
                value={shiftForm.start_time}
                onChange={(e) => setShiftForm(prev => ({ ...prev, start_time: e.target.value }))}
                className="bg-slate-800 border-slate-700 h-9 sm:h-10"
                data-testid="shift-start-time"
              />
            </div>
            <div className="space-y-1.5 sm:space-y-2">
              <Label className="text-sm">End</Label>
              <Input
                type="time"
                value={shiftForm.end_time}
                onChange={(e) => setShiftForm(prev => ({ ...prev, end_time: e.target.value }))}
                className="bg-slate-800 border-slate-700 h-9 sm:h-10"
                data-testid="shift-end-time"
              />
            </div>
          </div>

          {/* Title */}
          <div className="space-y-1.5 sm:space-y-2">
            <Label className="text-sm">Shift Title (Optional)</Label>
            <Input
              type="text"
              value={shiftForm.title}
              onChange={(e) => setShiftForm(prev => ({ ...prev, title: e.target.value }))}
              className="bg-slate-800 border-slate-700 h-9 sm:h-10"
              placeholder="e.g. Morning Shift"
              data-testid="shift-title"
            />
          </div>

          {/* Station & Role - side by side on mobile */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-1.5 sm:space-y-2">
              <Label className="text-sm">Station</Label>
              <Select 
                value={shiftForm.station} 
                onValueChange={(v) => setShiftForm(prev => ({ ...prev, station: v }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 h-9 sm:h-10" data-testid="shift-station-select">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {STATIONS.map(s => (
                    <SelectItem key={s.value} value={s.value} className="text-white">
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5 sm:space-y-2">
              <Label className="text-sm">Role</Label>
              <Select 
                value={shiftForm.role} 
                onValueChange={(v) => setShiftForm(prev => ({ ...prev, role: v }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 h-9 sm:h-10" data-testid="shift-role-select">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {ROLES.map(r => (
                    <SelectItem key={r.value} value={r.value} className="text-white">
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-1.5 sm:space-y-2">
            <Label className="text-sm">Notes (Optional)</Label>
            <Textarea
              value={shiftForm.notes}
              onChange={(e) => setShiftForm(prev => ({ ...prev, notes: e.target.value }))}
              className="bg-slate-800 border-slate-700 min-h-[60px] sm:min-h-[80px]"
              placeholder="Add notes..."
              data-testid="shift-notes"
            />
          </div>
        </div>
        
        <DialogFooter className="flex-col sm:flex-row gap-2">
          {editingShift && (
            <Button
              variant="destructive"
              onClick={onDelete}
              className="w-full sm:w-auto order-2 sm:order-1"
              data-testid="delete-shift-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          )}
          <div className="flex gap-2 w-full sm:w-auto order-1 sm:order-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-slate-700 flex-1 sm:flex-none"
            >
              Cancel
            </Button>
            <Button
              onClick={onSave}
              className="bg-emerald-600 hover:bg-emerald-700 flex-1 sm:flex-none"
              data-testid="save-shift-btn"
            >
              {editingShift ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { STATIONS, ROLES };
